import { cn } from "@/lib/utils";
import { cva, VariantProps } from "class-variance-authority";
import React from "react";

const circleVariants = cva(
  "inline-flex items-center justify-center font-bold rounded-full disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "text-muted-foreground hover:text-foreground hover:bg-ghost",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        none: "",
      },
      size: {
        xs: "min-w-4 min-h-4",
        sm: "min-w-6 min-h-6",
        md: "min-w-8 min-h-8",
        lg: "min-w-10 min-h-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "sm",
    },
  },
);

interface CircleProps extends VariantProps<typeof circleVariants> {}

export const Circle = React.forwardRef<
  HTMLDivElement,
  CircleProps &
    React.DetailedHTMLProps<
      React.HTMLAttributes<HTMLDivElement>,
      HTMLDivElement
    >
>(({ children, size, variant, className, ...props }, ref) => {
  return (
    <div
      {...props}
      ref={ref}
      className={cn(circleVariants({ variant, size, className }))}
    >
      {children}
    </div>
  );
});

export const CircleButton = React.forwardRef<
  HTMLButtonElement,
  CircleProps &
    React.DetailedHTMLProps<
      React.ButtonHTMLAttributes<HTMLButtonElement>,
      HTMLButtonElement
    >
>(({ children, size, variant, className, ...props }, ref) => {
  const tooltipAttributes = Object.fromEntries(
    Object.entries(props).filter(([key]) => key.startsWith("data-tooltip-")),
  );
  const buttonProps = Object.fromEntries(
    Object.entries(props).filter(([key]) => !key.startsWith("data-tooltip-")),
  );

  const button = (
    <button
      {...buttonProps}
      {...(!props.disabled ? tooltipAttributes : {})}
      ref={ref}
      className={cn(circleVariants({ variant, size, className }))}
    >
      {children}
    </button>
  );

  if (props.disabled && Object.keys(tooltipAttributes).length > 0) {
    return (
      <span {...tooltipAttributes} className="inline-flex">
        {button}
      </span>
    );
  }

  return button;
});
